import React, { ChangeEvent } from 'react';
import { FileUploader } from 'hq/components/views/upload-payments/shared/file-uploader';
import { useForm } from 'react-hook-form';
import { Box, Divider, styled, Typography } from '@mui/material';
import { LwButton } from 'redesign';
import {
  BulkShiftCreationCSVUploadPayload,
  BulkShiftCreationCSVUploadResponse,
} from 'ogp/services/shift-service.types';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'shared/components/alerts';
import { IconNotes } from 'assets/img';
import { useBulkShiftCreation } from './hooks/use-bulk-shift-creation';
import { ResultList } from './result-list';

type Props = {
  onClose: () => void;
};

export const BulkShiftCreationForm = ({ onClose: onClose }: Props) => {
  const { t } = useTranslation(['web-common', 'ogp-screen-specific']);
  const { mutate } = useBulkShiftCreation();
  const { alertSuccess, alertError } = useAlert();
  const { handleSubmit, setValue, watch } = useForm<BulkShiftCreationCSVUploadPayload>();
  const [response, setResponse] = React.useState<BulkShiftCreationCSVUploadResponse | undefined>(
    undefined
  );

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    const file = event.target.files[0];
    setValue('file', file);
  };

  const onSubmit = (payload: BulkShiftCreationCSVUploadPayload) => {
    mutate(payload, {
      onSuccess: (data) => {
        alertSuccess(t('csv.imported_successfully', { ns: 'web-common' }));
        setResponse(data);
      },
      onError: (e) => alertError(e),
    });
  };

  return (
    <FormContainer>
      <FullHeightForm name="work-form" onSubmit={handleSubmit(onSubmit)}>
        <FormBody>
          <Box>
            <Box>
              <Typography variant="h1" mb={4}>
                {t('planning.bulk_shift_creation.sidebar.title', { ns: 'ogp-screen-specific' })}
              </Typography>
            </Box>
            <StyledDivider />
            <FileUploader
              title="Upload CSV"
              fileType=".csv"
              onChange={handleFileUpload}
              disabled={false}
            ></FileUploader>
            {watch('file') ? (
              <>
                <Typography variant="body2" mt={6}>
                  {t('planning.bulk_shift_creation.selected_file', { ns: 'ogp-screen-specific' })}
                </Typography>
                <StyledFileBox>
                  <StyledIcon width={24} height={24} as={IconNotes} />
                  <Typography>{watch('file')?.name}</Typography>
                </StyledFileBox>
              </>
            ) : null}
          </Box>
          {response ? <ResultList results={response} /> : null}
        </FormBody>

        <SidebarActions>
          <LwButton color="secondary" onClick={onClose}>
            {t('buttons.cancel', { ns: 'web-common' })}
          </LwButton>
          <LwButton color="primary" type="submit" disabled={!watch('file')}>
            {t('buttons.publish', { ns: 'web-common' })}
          </LwButton>
        </SidebarActions>
      </FullHeightForm>
    </FormContainer>
  );
};

const FormContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(8),
}));

const FullHeightForm = styled('form')(() => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const FormBody = styled(Box)(() => ({
  flex: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
}));

const SidebarActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(8),
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const StyledFileBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(2),
  borderRadius: theme.spacing(10),
  marginTop: theme.spacing(2),
}));

const StyledIcon = styled('svg')(({ theme }) => ({
  '& path': {
    fill: theme.palette.lwSecondary[60],
  },
  marginRight: theme.spacing(4),
}));
